import { useState, useEffect } from "react";
import { Outlet, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  List, 
  X, 
  Phone, 
  EnvelopeSimple, 
  MapPin, 
  Globe,
  LinkedinLogo,
  TwitterLogo,
  FacebookLogo,
  InstagramLogo,
  ArrowUp,
  Leaf
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const navItems = [
  { label: "Home", href: "/" },
  { label: "About", href: "/about" },
  { label: "Services", href: "/services" },
  { label: "B2B Training", href: "/b2b-training" },
  { label: "TVET", href: "/tvet" },
  { label: "Programs", href: "/programs" },
  { label: "Careers", href: "/careers" },
  { label: "Projects", href: "/projects" },
  { label: "Blog", href: "/blog" },
  { label: "Partners", href: "/partnerships" },
  { label: "Gallery", href: "/gallery" },
  { label: "Contact", href: "/contact" },
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Layout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [email, setEmail] = useState("");
  const [subscribeStatus, setSubscribeStatus] = useState(null);
  const location = useLocation();
  const year = new Date().getFullYear();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
      setShowScrollTop(window.scrollY > 400);
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      setScrollProgress(totalHeight > 0 ? (window.scrollY / totalHeight) * 100 : 0);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/newsletter`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email })
      });
      const data = await res.json();
      setSubscribeStatus(data);
      if (data.status === "success") setEmail("");
    } catch (err) {
      setSubscribeStatus({ status: "error", message: "Failed to subscribe. Please try again." });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header 
        className={`fixed w-full z-50 transition-all duration-300 ${
          scrolled 
            ? "bg-white/95 backdrop-blur-xl border-b border-slate-200/50 shadow-sm" 
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 group transition-all duration-300" data-testid="logo-link">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-green-700 text-white shadow-lg group-hover:shadow-green-700/40 transition-shadow duration-300">
                <Leaf className="h-7 w-7" weight="duotone" />
              </div>
              <div>
                <div className="text-xl font-bold text-amber-500 tracking-tight group-hover:text-amber-600 transition-colors duration-300">PEDA AFRICA</div>
                <div className="text-[10px] uppercase tracking-[0.12em] text-amber-600/70 leading-tight group-hover:text-amber-700/80 transition-colors duration-300">
                  Enterprise. Skills. Community Transformation.
                </div>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              {navItems.map((item) => (
                <Link
                  key={item.label}
                  to={item.href}
                  data-testid={`nav-${item.label.toLowerCase()}-link`}
                  className={`relative px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                    location.pathname === item.href
                      ? "text-green-700 bg-green-50"
                      : "text-slate-600 hover:text-green-700"
                  } after:absolute after:bottom-0.5 after:left-1/2 after:-translate-x-1/2 after:h-[2px] after:bg-green-700 after:transition-all after:duration-300 ${
                    location.pathname === item.href
                      ? "after:w-5"
                      : "after:w-0 hover:after:w-5"
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </nav>

            {/* CTA Buttons */}
            <div className="hidden lg:flex items-center gap-3">
              <Button 
                asChild 
                variant="outline"
                className="border-amber-500 text-amber-600 hover:bg-amber-500 hover:text-white hover:border-amber-600 hover:scale-[1.03] rounded-xl px-5 transition-all duration-300"
                data-testid="donate-cta-button"
              >
                <Link to="/donate">Donate</Link>
              </Button>
              <Button 
                asChild 
                className="bg-green-700 hover:bg-green-800 hover:scale-[1.03] text-white rounded-xl px-6 shadow-lg shadow-green-700/20 hover:shadow-green-700/40 transition-all duration-300"
                data-testid="partner-cta-button"
              >
                <Link to="/contact">Get In Touch</Link>
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle menu"
              data-testid="mobile-menu-toggle"
            >
              {mobileOpen ? <X className="h-6 w-6" /> : <List className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden bg-white border-t border-slate-200 shadow-lg"
            >
              <div className="max-w-7xl mx-auto px-4 py-4 space-y-2">
                {navItems.map((item) => (
                  <Link
                    key={item.label}
                    to={item.href}
                    data-testid={`mobile-nav-${item.label.toLowerCase()}-link`}
                    className={`block px-4 py-3 text-base font-medium rounded-xl transition-all ${
                      location.pathname === item.href
                        ? "text-green-700 bg-green-50"
                        : "text-slate-700 hover:bg-slate-50"
                    }`}
                    onClick={() => setMobileOpen(false)}
                  >
                    {item.label}
                  </Link>
                ))}
                <Button 
                  asChild 
                  className="w-full bg-amber-500 hover:bg-amber-600 text-green-950 rounded-xl"
                >
                  <Link to="/donate" onClick={() => setMobileOpen(false)}>Donate</Link>
                </Button>
                <Button 
                  asChild 
                  className="w-full mt-2 bg-green-700 hover:bg-green-800 rounded-xl"
                >
                  <Link to="/contact" onClick={() => setMobileOpen(false)}>Get In Touch</Link>
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Scroll Progress Bar */}
      <div className="fixed top-0 left-0 right-0 z-[60] h-[3px] bg-transparent">
        <motion.div
          className="h-full bg-gradient-to-r from-green-600 via-amber-500 to-green-600"
          style={{ width: `${scrollProgress}%` }}
          transition={{ duration: 0.1 }}
        />
      </div>

      {/* Main Content */}
      <main>
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
          >
            <Outlet />
          </motion.div>
        </AnimatePresence>
      </main>

      {/* Footer */}
      <footer className="bg-green-950 text-white">
        {/* Main Footer */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {/* Company Info */}
            <div className="lg:col-span-1">
              <div className="flex items-center gap-3 mb-6">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-green-700 text-white">
                  <Leaf className="h-6 w-6" weight="duotone" />
                </div>
                <div>
                  <div className="text-lg font-bold">PEDA AFRICA</div>
                  <div className="text-xs text-green-300/80">Enterprise. Skills. Community Transformation.</div>
                </div>
              </div>
              <p className="text-green-100/70 text-sm leading-relaxed mb-6">
                PEDA AFRICA is a research-driven organization dedicated to fostering sustainable socio-economic development in Africa through entrepreneurship, innovation, and community-led enterprises.
              </p>
              <div className="flex gap-3">
                <a href="#" className="p-2 rounded-lg bg-green-900/50 hover:bg-amber-500 transition-colors" aria-label="Facebook">
                  <FacebookLogo className="h-5 w-5" weight="fill" />
                </a>
                <a href="#" className="p-2 rounded-lg bg-green-900/50 hover:bg-amber-500 transition-colors" aria-label="Twitter">
                  <TwitterLogo className="h-5 w-5" weight="fill" />
                </a>
                <a href="#" className="p-2 rounded-lg bg-green-900/50 hover:bg-amber-500 transition-colors" aria-label="LinkedIn">
                  <LinkedinLogo className="h-5 w-5" weight="fill" />
                </a>
                <a href="#" className="p-2 rounded-lg bg-green-900/50 hover:bg-amber-500 transition-colors" aria-label="Instagram">
                  <InstagramLogo className="h-5 w-5" weight="fill" />
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-lg font-semibold mb-6">Quick Links</h4>
              <ul className="space-y-3">
                {navItems.slice(0, 5).map((item) => (
                  <li key={item.label}>
                    <Link 
                      to={item.href} 
                      className="text-green-100/70 hover:text-amber-400 transition-colors text-sm footer-link"
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* More Links */}
            <div>
              <h4 className="text-lg font-semibold mb-6">Resources</h4>
              <ul className="space-y-3">
                {navItems.slice(5).map((item) => (
                  <li key={item.label}>
                    <Link 
                      to={item.href} 
                      className="text-green-100/70 hover:text-amber-400 transition-colors text-sm footer-link"
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
                <li>
                  <Link to="/about" className="text-green-100/70 hover:text-amber-400 transition-colors text-sm footer-link">
                    Vision & Mission
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="text-lg font-semibold mb-6">Contact Us</h4>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-amber-500 mt-0.5 shrink-0" weight="duotone" />
                  <span className="text-green-100/70 text-sm">Plot 62 Bombo Road, Kampala-Uganda</span>
                </li>
                <li className="flex items-start gap-3">
                  <Phone className="h-5 w-5 text-amber-500 mt-0.5 shrink-0" weight="duotone" />
                  <span className="text-green-100/70 text-sm">+256 782 565 502<br />+256 761 834 803</span>
                </li>
                <li className="flex items-start gap-3">
                  <EnvelopeSimple className="h-5 w-5 text-amber-500 mt-0.5 shrink-0" weight="duotone" />
                  <a href="mailto:info@pedafrica.org" className="text-green-100/70 hover:text-amber-400 text-sm">info@pedafrica.org</a>
                </li>
                <li className="flex items-start gap-3">
                  <Globe className="h-5 w-5 text-amber-500 mt-0.5 shrink-0" weight="duotone" />
                  <span className="text-green-100/70 text-sm">www.pedafrica.org</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Newsletter */}
          <div className="mt-12 pt-8 border-t border-green-900/50">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
              <div>
                <h4 className="text-lg font-semibold mb-2">Subscribe to our Newsletter</h4>
                <p className="text-green-100/60 text-sm">Stay updated on our programs and impact stories.</p>
              </div>
              <form onSubmit={handleSubscribe} className="flex gap-3 w-full md:w-auto">
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-green-900/30 border-green-800 text-white placeholder:text-green-300/50 rounded-xl h-12 w-full md:w-64"
                  data-testid="newsletter-email-input"
                />
                <Button 
                  type="submit" 
                  className="bg-amber-500 hover:bg-amber-600 text-green-950 font-semibold rounded-xl h-12 px-6"
                  data-testid="newsletter-submit-button"
                >
                  Subscribe
                </Button>
              </form>
            </div>
            {subscribeStatus && (
              <p className={`mt-3 text-sm ${subscribeStatus.status === "success" ? "text-green-400" : "text-amber-400"}`}>
                {subscribeStatus.message}
              </p>
            )}
          </div>
        </div>

        {/* Copyright */}
        <div className="border-t border-green-900/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-green-100/60">
              <p>&copy; {year} PEDA AFRICA. All rights reserved.</p>
              <p>Enterprise. Skills. Community Transformation.</p>
            </div>
          </div>
        </div>
      </footer>

      {/* Scroll to Top Button */}
      <AnimatePresence>
        {showScrollTop && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            onClick={scrollToTop}
            className="fixed bottom-8 right-8 p-3 bg-green-700 hover:bg-green-800 text-white rounded-full shadow-xl z-40 transition-colors"
            aria-label="Scroll to top"
            data-testid="scroll-to-top-button"
          >
            <ArrowUp className="h-6 w-6" weight="bold" />
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
}
